import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import { ProtectedRoute, AuthenticatedRoute } from './components/rbac';
import { OverviewOrStages } from './components/common/OverviewOrStages';
import { Spin } from 'antd';
import { NotFoundPage } from './pages/NotFound/NotFoundPage';

// Главная — грузится сразу (стартовый экран)
import MainPage from './pages/Demo/MainPage';

// Планирование продаж
const SalesPlanningStages = lazy(() => import('./pages/SalesPlanning/SalesPlanningPage'));
const SalesOverviewPage   = lazy(() => import('./pages/SalesPlanning/SalesOverviewPage'));

// Планирование закупок
const PurchasePage        = lazy(() => import('./pages/PurchasePlanning/PurchasePage'));
const PurchaseOverviewPage= lazy(() => import('./pages/PurchasePlanning/PurchaseOverviewPage'));

// Сверка
const ReconciliationPage  = lazy(() => import('./pages/Reconciliation/ReconciliationPage'));

// Справочники
const ReferencesPage      = lazy(() => import('./pages/References/ReferencesPage'));
const ReferencesOverviewPage = lazy(() => import('./pages/References/ReferencesOverviewPage'));

// Админка (редко используется — хорошая цель для lazy)
const AdminDashboard      = lazy(() => import('./pages/Admin/Dashboard').then(m => ({ default: m.AdminDashboard })));
const Monitoring          = lazy(() => import('./pages/Admin/Monitoring').then(m => ({ default: m.Monitoring })));
const ErrorsPage          = lazy(() => import('./pages/Admin/Errors').then(m => ({ default: m.Errors })));
const UsersManagement     = lazy(() => import('./pages/Admin/components/UsersManagement').then(m => ({ default: m.UsersManagement })));
const RolesManagement     = lazy(() => import('./pages/Admin/components/RolesManagement').then(m => ({ default: m.RolesManagement })));
const RolePermissionsPage = lazy(() => import('./pages/Admin/components/RolePermissionsPage').then(m => ({ default: m.RolePermissionsPage })));
const EntraSync           = lazy(() => import('./pages/Admin/EntraSync').then(m => ({ default: m.EntraSync })));
const AuditLogs           = lazy(() => import('./pages/Admin/AuditLogs').then(m => ({ default: m.AuditLogs })));
const FeatureFlags        = lazy(() => import('./pages/Admin/FeatureFlags').then(m => ({ default: m.FeatureFlags })));
const PageComments        = lazy(() => import('./pages/Admin/PageComments').then(m => ({ default: m.PageComments })));
const UIConfigPage        = lazy(() => import('./pages/Admin/UIConfig/UIConfigPage').then(m => ({ default: m.UIConfigPage })));
const EmailNotifications  = lazy(() => import('./pages/Admin/EmailNotifications').then(m => ({ default: m.EmailNotifications })));
const MethodologyAdmin    = lazy(() => import('./pages/Admin/Methodology').then(m => ({ default: m.MethodologyAdmin })));

// Login
const LoginPage           = lazy(() => import('./pages/Login/LoginPage'));

// Методология (вики)
const MethodologyPage     = lazy(() => import('./pages/Methodology/MethodologyPage'));

// Planning Flow
const PlanningFlowPage    = lazy(() => import('./pages/PlanningFlow/PlanningFlowPage'));

// Прочие страницы
const NotificationsPage   = lazy(() => import('./pages/Notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const SettingsPage        = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })));

export const AppRoutes: React.FC = () => (
  <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}><Spin size="large" /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<MainLayout />}>
        <Route index element={<AuthenticatedRoute><MainPage /></AuthenticatedRoute>} />

        {/* Планирование продаж */}
        <Route path="sales">
          <Route index element={
            <ProtectedRoute resource="sales" permission="read" checkChildren>
              <OverviewOrStages
                overviewComponent={SalesOverviewPage}
                stagesComponent={SalesPlanningStages}
                redirectPath="/sales/stage-1"
              />
            </ProtectedRoute>
          } />
          <Route path=":stageId" element={
            <ProtectedRoute resource="sales" permission="read" checkChildren>
              <SalesPlanningStages />
            </ProtectedRoute>
          } />
        </Route>

        {/* Планирование закупок */}
        <Route path="purchase">
          <Route index element={
            <ProtectedRoute resource="purchase" permission="read" checkChildren>
              <OverviewOrStages
                overviewComponent={PurchaseOverviewPage}
                stagesComponent={PurchasePage}
                redirectPath="/purchase/stage-1"
              />
            </ProtectedRoute>
          } />
          <Route path=":stageId" element={
            <ProtectedRoute resource="purchase" permission="read" checkChildren>
              <PurchasePage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Сверка */}
        <Route path="reconciliation">
          <Route index element={
            <ProtectedRoute resource="reconciliation" permission="read">
              <Navigate to="/reconciliation/overview" replace />
            </ProtectedRoute>
          } />
          <Route path=":stageId" element={
            <ProtectedRoute resource="reconciliation" permission="read">
              <ReconciliationPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Справочники */}
        <Route path="references">
          <Route index element={
            <ProtectedRoute resource="references" permission="read" checkChildren>
              <OverviewOrStages
                overviewComponent={ReferencesOverviewPage}
                stagesComponent={ReferencesPage}
                redirectPath="/references/assortment"
              />
            </ProtectedRoute>
          } />
          <Route path=":moduleId" element={
            <ProtectedRoute resource="references" permission="read" checkChildren>
              <ReferencesPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Методология */}
        <Route path="methodology">
          <Route index element={
            <ProtectedRoute resource="methodology" permission="read">
              <MethodologyPage />
            </ProtectedRoute>
          } />
          <Route path=":pageId" element={
            <ProtectedRoute resource="methodology" permission="read">
              <MethodologyPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Админ-панель */}
        <Route path="admin" element={
          <ProtectedRoute resource="admin" permission="manage">
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="errors" element={<ErrorsPage />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="roles" element={<RolesManagement />} />
          <Route path="roles/:roleValue/permissions" element={<RolePermissionsPage />} />
          <Route path="sync" element={<EntraSync />} />
          <Route path="audit" element={<AuditLogs />} />
          <Route path="feature-flags" element={<FeatureFlags />} />
          <Route path="page-comments" element={<PageComments />} />
          <Route path="ui-config" element={<UIConfigPage />} />
          <Route path="email-notifications" element={<EmailNotifications />} />
          <Route path="methodology" element={<MethodologyAdmin />} />
        </Route>

        {/* Planning Flow */}
        <Route path="flow" element={
          <AuthenticatedRoute><PlanningFlowPage /></AuthenticatedRoute>
        } />

        {/* Прочие страницы */}
        <Route path="notifications" element={
          <AuthenticatedRoute><NotificationsPage /></AuthenticatedRoute>
        } />
        <Route path="settings" element={
          <AuthenticatedRoute><SettingsPage /></AuthenticatedRoute>
        } />
        <Route path="health" element={<Navigate to="/admin/monitoring" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  </Suspense>
);
